/* global React */
/* ──────────────────────────────────────────────────────────────────────
   Footer — site-wide bottom bar, shared across every page.
   Reads its data from window.DZ_FOOTER (content/footer.js).
   ────────────────────────────────────────────────────────────────────── */
const { useState } = React;

const FtrPhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"/>
  </svg>
);

function Footer() {
  const data = window.DZ_FOOTER;

  return (
    <footer className="ftr" role="contentinfo">
      <div className="ftr__inner">

        <div className="ftr__top">
          <a href={window.location.pathname.startsWith('/ar/') ? '/ar/' : '/'} className="ftr-logo" aria-label={`${data.brand.logoAlt} home`}>
            <img src={data.brand.logo} alt={data.brand.logoAlt} width="1750" height="461" loading="lazy" decoding="async" />
          </a>
        </div>

        <div className="ftr__main">
          <div className="ftr-brand">
            <p className="ftr-brand__tag" dangerouslySetInnerHTML={{ __html: data.brand.tagline }} />
            <div className="ftr-endorse" aria-label="Accreditations">
              <span className="ftr-endorse__label">{data.brand.endorseLabel}</span>
              {data.brand.endorsements.map(e => (
                <img key={e.alt} src={e.src} alt={e.alt} title={e.title} loading="lazy" decoding="async" />
              ))}
            </div>
            <ul className="ftr-reach__list">
              <li>
                <a href={`mailto:${data.brand.email}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2"/>
                    <path d="m3 7 9 6 9-6"/>
                  </svg>
                  {data.brand.email}
                </a>
              </li>
              <li>
                <a href={`tel:${data.brand.phoneTel}`}>
                  <FtrPhoneIcon />
                  {data.brand.phone}
                </a>
              </li>
            </ul>
          </div>

          {data.nav.map(col => (
            <nav key={col.heading} aria-label={col.heading}>
              <h4 className="ftr-col__heading">{col.heading}</h4>
              <ul className="ftr-col__list">
                {col.items.map(([label, href]) => (
                  <li key={href}><a href={href}>{label}</a></li>
                ))}
              </ul>
            </nav>
          ))}

          <nav aria-label="Office locations">
            <h4 className="ftr-col__heading">{data.officesHeading}</h4>
            <ul className="ftr-col__list ftr-col__list--offices">
              {data.offices.map(o => (
                <li key={o.id} aria-label={`${o.city} office details`}>
                  <span className="ftr-office__city">
                    {o.city}
                    {o.hq && <span className="ftr-office__hq">HQ</span>}
                  </span>
                  <span className="ftr-office__country">{o.country}</span>
                  <span className="ofc-pop" role="tooltip">
                    <span className="ofc-pop__city">{o.city} · {o.country}</span>
                    <p className="ofc-pop__addr">{o.addr}</p>
                    <a className="ofc-pop__phone" href={`tel:${o.tel}`}>
                      <FtrPhoneIcon />
                      {o.phone}
                    </a>
                  </span>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="ftr__bottom">
          <div className="ftr-legal">
            <span>{data.copyright}</span>
            {data.legalLinks.map(link => (
              <React.Fragment key={link.href}>
                <span className="sep"></span><a href={link.href}>{link.label}</a>
              </React.Fragment>
            ))}
            {/* Cookie preferences — re-opens the consent banner via the
                global hook installed by /js/cookie-consent.js. PDPL § 14
                + GDPR Art. 7 give users the right to withdraw consent
                at any time — this is the entry point. */}
            {data.cookieLabel && (
              <React.Fragment>
                <span className="sep"></span>
                <button
                  type="button"
                  className="ftr-legal__cookie"
                  onClick={() => typeof window !== 'undefined' && typeof window.DZ_openConsent === 'function' && window.DZ_openConsent()}
                >
                  {data.cookieLabel}
                </button>
              </React.Fragment>
            )}
          </div>
          <div className="ftr-social" aria-label="Design Zone on social media">
            <a href={data.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
            </a>
            <a href={data.social.x} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" title="X">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href={data.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href={data.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href={data.social.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" title="WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}

window.Footer = Footer;
